import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { Resource, ResourcesResponse } from "../../models/Resources.model";
import { getResources } from "../thunks/getResourcesThunks";
import {
  createResource,
  updateResource,
  deleteResource,
} from "../thunks/resourceThunks";

interface ResourcesState {
  resources: Resource[];
  loading: boolean;
  operationLoading: boolean;
  error: string | null;
  success: boolean;
  message: string;
}

const initialState: ResourcesState = {
  resources: [],
  loading: false,
  operationLoading: false,
  error: null,
  success: false,
  message: "",
};

const resourcesSlice = createSlice({
  name: "resources",
  initialState,
  reducers: {
    clearMessages: (state) => {
      state.error = null;
      state.message = "";
      state.success = false;
    },
    resetResourcesState: (state) => {
      Object.assign(state, initialState);
    },
    setDefaultResource: (state, action: PayloadAction<Resource>) => {
      // מעדכן רק את הרשימה הראשית
      state.resources = state.resources.map((r) =>
        r.resourceID === action.payload.resourceID
          ? { ...r, isDefault: true }
          : { ...r, isDefault: false }
      );
    },
    clearResourcesOnCompanyChange: (state) => {
      state.resources = [];
      state.error = null;
      state.message = "";
      state.success = false;
    },
  },
  extraReducers: (builder) => {
    builder
      // Get Resources
      .addCase(getResources.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.success = false;
      })
      .addCase(
        getResources.fulfilled,
        (state, action: PayloadAction<ResourcesResponse>) => {
          state.loading = false;
          state.success = true;
          state.resources = action.payload.resources;
          state.message = action.payload.message;
        }
      )
      .addCase(getResources.rejected, (state, action) => {
        state.loading = false;
        state.success = false;
        state.error = (action.payload as string) || "Failed to fetch resources";
      })

      // Create Resource
      .addCase(createResource.pending, (state) => {
        state.operationLoading = true;
        state.error = null;
        state.success = false;
      })
      .addCase(createResource.fulfilled, (state, action) => {
        state.operationLoading = false;
        state.success = true;
        state.message = action.payload.message;

        const requestData = action.meta.arg;
        const newResource: Resource = {
          resourceID: action.payload.resourceID,
          resourceName: requestData.resourceName,
          resourceColor: requestData.resourceColor,
          minutesOffset: requestData.minutesOffset,
          allowOverflow: requestData.allowOverflow,
          isDefault: requestData.isDefault,
        };

        state.resources.push(newResource);

        // עדכון ברירת מחדל
        if (newResource.isDefault) {
          state.resources = state.resources.map((r) =>
            r.resourceID !== newResource.resourceID
              ? { ...r, isDefault: false }
              : r
          );
        }
      })
      .addCase(createResource.rejected, (state, action) => {
        state.operationLoading = false;
        state.success = false;
        state.error = (action.payload as string) || "Failed to create resource";
      })

      // Update Resource
      .addCase(updateResource.pending, (state) => {
        state.operationLoading = true;
        state.error = null;
        state.success = false;
      })
      .addCase(updateResource.fulfilled, (state, action) => {
        state.operationLoading = false;
        state.success = true;
        state.message = action.payload.message;

        const requestData = action.meta.arg;
        const updatedResource: Resource = {
          resourceID: requestData.resourceID,
          resourceName: requestData.resourceName,
          resourceColor: requestData.resourceColor,
          minutesOffset: requestData.minutesOffset,
          allowOverflow: requestData.allowOverflow,
          isDefault: requestData.isDefault,
        };

        // עדכון במערך הראשי
        const index = state.resources.findIndex(
          (r) => r.resourceID === updatedResource.resourceID
        );
        if (index !== -1) {
          state.resources[index] = updatedResource;
        }

        // עדכון ברירת מחדל
        if (updatedResource.isDefault) {
          state.resources = state.resources.map((r) =>
            r.resourceID !== updatedResource.resourceID
              ? { ...r, isDefault: false }
              : r
          );
        }
      })
      .addCase(updateResource.rejected, (state, action) => {
        state.operationLoading = false;
        state.success = false;
        state.error = (action.payload as string) || "Failed to update resource";
      })

      // Delete Resource
      .addCase(deleteResource.pending, (state) => {
        state.operationLoading = true;
        state.error = null;
        state.success = false;
      })
      .addCase(deleteResource.fulfilled, (state, action) => {
        state.operationLoading = false;
        state.success = true;
        state.message = action.payload.message;

        const deletedResourceID = action.payload.resourceID;
        state.resources = state.resources.filter(
          (r) => r.resourceID !== deletedResourceID
        );
      })
      .addCase(deleteResource.rejected, (state, action) => {
        state.operationLoading = false;
        state.success = false;
        state.error = (action.payload as string) || "Failed to delete resource";
      });
  },
});

export const {
  clearMessages,
  resetResourcesState,
  setDefaultResource,
  clearResourcesOnCompanyChange, // הוסף את זה
} = resourcesSlice.actions;

export default resourcesSlice.reducer;

// Selectors
export const selectResources = (state: { resources: ResourcesState }) =>
  state.resources.resources;
export const selectResourcesLoading = (state: { resources: ResourcesState }) =>
  state.resources.loading;
export const selectResourcesError = (state: { resources: ResourcesState }) =>
  state.resources.error;
export const selectResourcesSuccess = (state: { resources: ResourcesState }) =>
  state.resources.success;
export const selectOperationLoading = (state: { resources: ResourcesState }) =>
  state.resources.operationLoading;
export const selectResourcesMessage = (state: { resources: ResourcesState }) =>
  state.resources.message;
